import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, Trash2, X, FolderPlus, Users, Globe, Search, FileUp } from "lucide-react";
import ClientTargetPicker, { ClientTargetModal } from "@/components/ClientTargetPicker";
import BulkActionBar, { SelectCheckbox } from "@/components/BulkActionBar";
import TarifsImportModal from "@/components/TarifsImportModal";

const normalize = (s) =>
    (s || "")
        .toString()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/œ/g, "oe")
        .replace(/æ/g, "ae");

export default function AdminTarifs() {
    const qc = useQueryClient();
    const [addingItem, setAddingItem] = useState(null); // category_id
    const [newItem, setNewItem] = useState({ code: "", article: "", provenance: "Suisse", prix_kg_ht: "", unite: "kg", target_user_ids: [] });
    const [showNewCat, setShowNewCat] = useState(false);
    const [newCat, setNewCat] = useState({ nom: "", ordre: 999 });
    const [openCat, setOpenCat] = useState(null); // which category expanded on mobile
    const [showTargetPickerNew, setShowTargetPickerNew] = useState(false);
    const [targetingItem, setTargetingItem] = useState(null); // existing item being retargeted
    const [search, setSearch] = useState("");
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [importOpen, setImportOpen] = useState(false);

    const { data: categories = [] } = useQuery({
        queryKey: ["admin-categories"],
        queryFn: async () => (await api.get("/admin/categories")).data,
    });
    const { data: items = [] } = useQuery({
        queryKey: ["admin-prices"],
        queryFn: async () => (await api.get("/admin/prices")).data,
    });
    const { data: pros = [] } = useQuery({
        queryKey: ["admin-users", "approved"],
        queryFn: async () => (await api.get("/admin/users?status_filter=approved")).data,
    });
    const prosById = useMemo(() => Object.fromEntries((pros || []).map((p) => [p.id, p])), [pros]);

    const updateItem = useMutation({
        mutationFn: ({ id, data }) => api.put(`/admin/prices/${id}`, data),
        onSuccess: () => {
            toast.success("Article enregistré");
            qc.invalidateQueries({ queryKey: ["admin-prices"] });
            qc.invalidateQueries({ queryKey: ["prices"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const deleteItem = useMutation({
        mutationFn: (id) => api.delete(`/admin/prices/${id}`),
        onSuccess: () => {
            toast.success("Article supprimé");
            qc.invalidateQueries({ queryKey: ["admin-prices"] });
            qc.invalidateQueries({ queryKey: ["prices"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const createItem = useMutation({
        mutationFn: (data) => api.post("/admin/prices", data),
        onSuccess: () => {
            toast.success("Article créé");
            setAddingItem(null);
            setShowTargetPickerNew(false);
            setNewItem({ code: "", article: "", provenance: "Suisse", prix_kg_ht: "", unite: "kg", target_user_ids: [] });
            qc.invalidateQueries({ queryKey: ["admin-prices"] });
            qc.invalidateQueries({ queryKey: ["prices"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const createCategory = useMutation({
        mutationFn: (data) => api.post("/admin/categories", data),
        onSuccess: () => {
            toast.success("Catégorie créée");
            setShowNewCat(false);
            setNewCat({ nom: "", ordre: 999 });
            qc.invalidateQueries({ queryKey: ["admin-categories"] });
            qc.invalidateQueries({ queryKey: ["categories"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const deleteCategory = useMutation({
        mutationFn: (id) => api.delete(`/admin/categories/${id}`),
        onSuccess: () => {
            toast.success("Catégorie supprimée");
            qc.invalidateQueries({ queryKey: ["admin-categories"] });
            qc.invalidateQueries({ queryKey: ["admin-prices"] });
            qc.invalidateQueries({ queryKey: ["categories"] });
            qc.invalidateQueries({ queryKey: ["prices"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const byCat = useMemo(() => {
        const q = normalize(search.trim());
        const g = {};
        for (const c of categories) g[c.id] = [];
        for (const it of items) {
            if (!g[it.category_id]) continue;
            if (q) {
                const hay = normalize(`${it.article} ${it.code} ${it.provenance}`);
                if (!hay.includes(q)) continue;
            }
            g[it.category_id].push(it);
        }
        for (const cid in g) g[cid].sort((a, b) => (a.code || "").localeCompare(b.code || ""));
        return g;
    }, [items, categories, search]);

    const matchedCount = useMemo(() => Object.values(byCat).reduce((n, arr) => n + arr.length, 0), [byCat]);

    const submitNew = (categoryId) => {
        const p = {
            ...newItem,
            category_id: categoryId,
            prix_kg_ht: newItem.prix_kg_ht === "" ? null : Number(newItem.prix_kg_ht),
            target_user_ids: newItem.target_user_ids || [],
        };
        if (!p.article.trim()) {
            toast.error("Le nom de l'article est requis");
            return;
        }
        createItem.mutate(p);
    };

    const saveTargeting = (nextIds) => {
        if (!targetingItem) return;
        updateItem.mutate({
            id: targetingItem.id,
            data: {
                ...targetingItem,
                target_user_ids: nextIds,
                prix_kg_ht: targetingItem.prix_kg_ht == null ? null : Number(targetingItem.prix_kg_ht),
            },
        });
        setTargetingItem(null);
    };

    const bulkDelete = useMutation({
        mutationFn: (ids) => api.post("/admin/prices/bulk-delete", { ids }),
        onSuccess: (res) => {
            toast.success(`${res.data?.deleted ?? 0} article(s) supprimé(s)`);
            setSelectedIds(new Set());
            qc.invalidateQueries({ queryKey: ["admin-prices"] });
            qc.invalidateQueries({ queryKey: ["prices"] });
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const visibleItems = useMemo(() => {
        const out = [];
        for (const cat of categories) {
            for (const it of byCat[cat.id] || []) out.push(it);
        }
        return out;
    }, [byCat, categories]);
    const allSelected = visibleItems.length > 0 && visibleItems.every((it) => selectedIds.has(it.id));
    const toggleSelect = (id, checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
    };
    const toggleAll = () => {
        if (allSelected) setSelectedIds(new Set());
        else setSelectedIds(new Set(visibleItems.map((it) => it.id)));
    };
    const clearSelection = () => setSelectedIds(new Set());
    const onBulkDelete = () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (window.confirm(`Supprimer définitivement ${ids.length} article${ids.length > 1 ? "s" : ""} ?`)) {
            bulkDelete.mutate(ids);
        }
    };

    return (
        <div data-testid="admin-tarifs">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="font-serif text-2xl text-[#D4BC7C]">Tarifs</h2>
                    <p className="text-sm text-[#A0A0A0] mt-2">
                        Modifiez les prix directement dans la ligne. Les changements sont visibles instantanément côté pro.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button
                        className="bc-btn bc-btn--sm"
                        onClick={() => setImportOpen(true)}
                        data-testid="btn-import-pdf"
                        title="Mettre à jour les tarifs en masse depuis un PDF"
                    >
                        <FileUp size={14} /> Import PDF
                    </button>
                    <button className="bc-btn bc-btn--sm" onClick={() => setShowNewCat(true)} data-testid="btn-new-category">
                        <FolderPlus size={14} /> Nouvelle catégorie
                    </button>
                </div>
            </div>

            {showNewCat && (
                <div className="bc-card mt-6" data-testid="new-category-form">
                    <p className="bc-label">Nouvelle catégorie</p>
                    <div className="grid grid-cols-1 sm:grid-cols-[2fr,1fr,auto,auto] gap-3 items-end">
                        <input
                            className="bc-input"
                            placeholder="Nom (ex. Gibier)"
                            value={newCat.nom}
                            onChange={(e) => setNewCat({ ...newCat, nom: e.target.value })}
                            data-testid="new-cat-nom"
                        />
                        <input
                            className="bc-input"
                            type="number"
                            placeholder="Ordre"
                            value={newCat.ordre}
                            onChange={(e) => setNewCat({ ...newCat, ordre: Number(e.target.value) })}
                            data-testid="new-cat-ordre"
                        />
                        <button
                            className="bc-btn bc-btn--sm"
                            onClick={() => createCategory.mutate(newCat)}
                            disabled={!newCat.nom.trim()}
                            data-testid="new-cat-save"
                        >
                            <Save size={14} /> Créer
                        </button>
                        <button className="bc-btn bc-btn--sm bc-btn--ghost" onClick={() => setShowNewCat(false)}>
                            <X size={14} />
                        </button>
                    </div>
                </div>
            )}

            <div className="mt-8">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4BC7C] pointer-events-none z-10" size={16} />
                    <input
                        className="bc-input"
                        style={{ paddingLeft: "2.5rem" }}
                        placeholder="Rechercher un article (code, nom, provenance)…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="admin-tarifs-search"
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#D4BC7C]"
                            data-testid="admin-tarifs-search-clear"
                            aria-label="Effacer la recherche"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
                {search && (
                    <p className="text-xs text-[#A0A0A0] mt-2" data-testid="admin-tarifs-search-count">
                        {matchedCount} résultat{matchedCount > 1 ? "s" : ""} pour «&nbsp;{search}&nbsp;»
                    </p>
                )}
            </div>

            <div className="mt-6">
                <BulkActionBar
                    selectedCount={selectedIds.size}
                    totalCount={visibleItems.length}
                    allSelected={allSelected}
                    onToggleAll={toggleAll}
                    onClear={clearSelection}
                    onBulkDelete={onBulkDelete}
                    disabled={bulkDelete.isPending}
                    label="article"
                />
            </div>

            <div className="mt-4 space-y-10">
                {categories.map((cat) => {
                    // When searching, hide categories with 0 matches
                    if (search && (byCat[cat.id] || []).length === 0) return null;
                    return (
                    <section key={cat.id} data-testid={`admin-cat-${cat.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                            <div className="flex items-center gap-4 flex-wrap">
                                <h3 className="font-serif text-[#D4BC7C] text-2xl">{cat.nom}</h3>
                                <span className="text-xs text-[#A0A0A0] uppercase tracking-widest">
                                    {(byCat[cat.id] || []).length} articles
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    className="bc-btn bc-btn--sm"
                                    onClick={() => setAddingItem(cat.id)}
                                    data-testid={`btn-add-item-${cat.id}`}
                                >
                                    <Plus size={14} /> Ajouter
                                </button>
                                <button
                                    className="bc-btn bc-btn--sm bc-btn--danger"
                                    onClick={() => {
                                        if (window.confirm(`Supprimer la catégorie "${cat.nom}" et tous ses articles ?`)) {
                                            deleteCategory.mutate(cat.id);
                                        }
                                    }}
                                    data-testid={`btn-delete-cat-${cat.id}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                        <div className="bc-hairline mb-4" />

                        {addingItem === cat.id && (
                            <div className="bc-card mb-4" data-testid={`new-item-form-${cat.id}`}>
                                <p className="bc-label">Nouvel article</p>
                                <div className="grid grid-cols-1 md:grid-cols-[110px,1fr,180px,140px,90px,auto,auto] gap-2">
                                    <input className="bc-input" placeholder="Code" value={newItem.code} onChange={(e) => setNewItem({ ...newItem, code: e.target.value })} data-testid="new-item-code" />
                                    <input className="bc-input" placeholder="Article" value={newItem.article} onChange={(e) => setNewItem({ ...newItem, article: e.target.value })} data-testid="new-item-article" />
                                    <input className="bc-input" placeholder="Provenance" value={newItem.provenance} onChange={(e) => setNewItem({ ...newItem, provenance: e.target.value })} />
                                    <input className="bc-input" type="number" step="0.01" placeholder="Prix (vide = sur demande)" value={newItem.prix_kg_ht} onChange={(e) => setNewItem({ ...newItem, prix_kg_ht: e.target.value })} data-testid="new-item-prix" />
                                    <input className="bc-input" placeholder="Unité" value={newItem.unite} onChange={(e) => setNewItem({ ...newItem, unite: e.target.value })} />
                                    <button className="bc-btn bc-btn--sm" onClick={() => submitNew(cat.id)} data-testid={`save-new-item-${cat.id}`}>
                                        <Save size={14} />
                                    </button>
                                    <button className="bc-btn bc-btn--sm bc-btn--ghost" onClick={() => { setAddingItem(null); setShowTargetPickerNew(false); }}>
                                        <X size={14} />
                                    </button>
                                </div>

                                <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
                                    <div className="flex items-center gap-2 text-xs">
                                        {(newItem.target_user_ids || []).length === 0 ? (
                                            <span className="inline-flex items-center gap-1.5 text-[#A0A0A0]">
                                                <Globe size={12} /> Article public — visible par tous les pros
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 text-[#D4BC7C]">
                                                <Users size={12} />
                                                Exclusif · {newItem.target_user_ids.length} client{newItem.target_user_ids.length > 1 ? "s" : ""}
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className="text-[10px] text-[#D4BC7C] uppercase tracking-widest hover:underline"
                                        onClick={() => setShowTargetPickerNew((v) => !v)}
                                        data-testid={`toggle-target-new-${cat.id}`}
                                    >
                                        {showTargetPickerNew ? "Masquer le ciblage" : "Cibler des clients spécifiques"}
                                    </button>
                                </div>

                                {showTargetPickerNew && (
                                    <div className="mt-4">
                                        <ClientTargetPicker
                                            pros={pros}
                                            value={newItem.target_user_ids || []}
                                            onChange={(ids) => setNewItem({ ...newItem, target_user_ids: ids })}
                                            compact
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="space-y-2">
                            {(byCat[cat.id] || []).map((it) => (
                                <PriceRow
                                    key={`${it.id}-${it.updated_at || ""}`}
                                    item={it}
                                    prosById={prosById}
                                    selected={selectedIds.has(it.id)}
                                    onToggleSelect={(v) => toggleSelect(it.id, v)}
                                    onSave={(data) => updateItem.mutate({ id: it.id, data: { ...it, ...data, prix_kg_ht: data.prix_kg_ht === "" ? null : Number(data.prix_kg_ht) } })}
                                    onDelete={() => {
                                        if (window.confirm(`Supprimer "${it.article}" ?`)) deleteItem.mutate(it.id);
                                    }}
                                    onOpenTarget={() => setTargetingItem(it)}
                                />
                            ))}
                        </div>
                    </section>
                    );
                })}
                {search && matchedCount === 0 && (
                    <p className="text-[#A0A0A0]" data-testid="admin-tarifs-empty">
                        Aucun article ne correspond à «&nbsp;{search}&nbsp;».
                    </p>
                )}
            </div>

            <ClientTargetModal
                open={!!targetingItem}
                pros={pros}
                initialValue={targetingItem?.target_user_ids || []}
                itemLabel={targetingItem ? `${targetingItem.code ? targetingItem.code + " · " : ""}${targetingItem.article}` : ""}
                onClose={() => setTargetingItem(null)}
                onSave={saveTargeting}
            />

            <TarifsImportModal
                open={importOpen}
                onClose={() => setImportOpen(false)}
            />
        </div>
    );
}

function PriceRow({ item, prosById, selected, onToggleSelect, onSave, onDelete, onOpenTarget }) {
    const [local, setLocal] = useState({
        code: item.code || "",
        article: item.article || "",
        provenance: item.provenance || "",
        prix_kg_ht: item.prix_kg_ht == null ? "" : item.prix_kg_ht,
        unite: item.unite || "kg",
    });
    const dirty =
        String(local.code) !== String(item.code || "") ||
        local.article !== (item.article || "") ||
        local.provenance !== (item.provenance || "") ||
        String(local.prix_kg_ht) !== String(item.prix_kg_ht == null ? "" : item.prix_kg_ht) ||
        local.unite !== (item.unite || "kg");

    const targets = item.target_user_ids || [];
    const isExclusive = targets.length > 0;
    const targetLabels = targets
        .map((id) => prosById?.[id]?.raison_sociale || prosById?.[id]?.email || null)
        .filter(Boolean);
    const badgeText = !isExclusive
        ? "Public"
        : targets.length === 1
            ? `Exclusif : ${targetLabels[0] || "1 client"}`
            : `Exclusif · ${targets.length} clients`;
    const badgeTooltip = isExclusive && targetLabels.length > 0 ? targetLabels.join(", ") : "";

    return (
        <div
            className="border p-2"
            style={{ borderColor: dirty ? "var(--bc-gold)" : (selected ? "rgba(212,188,124,0.5)" : "var(--bc-border)"), background: selected ? "rgba(212,188,124,0.04)" : "#0a0a0a" }}
            data-testid={`price-row-${item.id}`}
        >
            <div className="grid grid-cols-1 md:grid-cols-[28px,110px,1fr,180px,140px,90px,auto,auto,auto] gap-2 items-center">
                <SelectCheckbox
                    checked={!!selected}
                    onChange={(v) => onToggleSelect(v)}
                    testId={`select-price-${item.id}`}
                />
                <input className="bc-input" value={local.code} onChange={(e) => setLocal({ ...local, code: e.target.value })} placeholder="Code" />
                <input className="bc-input" value={local.article} onChange={(e) => setLocal({ ...local, article: e.target.value })} data-testid={`edit-article-${item.id}`} />
                <input className="bc-input" value={local.provenance} onChange={(e) => setLocal({ ...local, provenance: e.target.value })} />
                <input
                    className="bc-input"
                    type="number"
                    step="0.01"
                    placeholder="Sur demande"
                    value={local.prix_kg_ht}
                    onChange={(e) => setLocal({ ...local, prix_kg_ht: e.target.value })}
                    data-testid={`edit-prix-${item.id}`}
                />
                <input className="bc-input" value={local.unite} onChange={(e) => setLocal({ ...local, unite: e.target.value })} />
                <button
                    className="bc-btn bc-btn--sm bc-btn--ghost"
                    onClick={onOpenTarget}
                    disabled={dirty}
                    title={dirty ? "Enregistrez d'abord vos modifications de la ligne" : "Ciblage clients"}
                    data-testid={`target-${item.id}`}
                >
                    <Users size={14} />
                </button>
                <button
                    className="bc-btn bc-btn--sm"
                    disabled={!dirty}
                    onClick={() => onSave(local)}
                    data-testid={`save-${item.id}`}
                >
                    <Save size={14} /> Enregistrer
                </button>
                <button className="bc-btn bc-btn--sm bc-btn--danger" onClick={onDelete} data-testid={`delete-${item.id}`}>
                    <Trash2 size={14} />
                </button>
            </div>

            <div className="mt-2 flex items-center gap-2">
                <span
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 border"
                    style={{
                        color: isExclusive ? "#D4BC7C" : "#A0A0A0",
                        borderColor: isExclusive ? "var(--bc-gold)" : "var(--bc-border)",
                        background: isExclusive ? "rgba(212,188,124,0.06)" : "transparent",
                    }}
                    title={badgeTooltip}
                    data-testid={`badge-target-${item.id}`}
                >
                    {isExclusive ? <Users size={11} /> : <Globe size={11} />}
                    {badgeText}
                </span>
            </div>
        </div>
    );
}
